import { useNavigate, useSearch } from "@tanstack/react-router"
import { ArrowRight, ChevronRight, Loader2, LogIn, TriangleAlert } from "lucide-react"
import { type ReactNode, useEffect, useId, useState } from "react"
import { Trans } from "react-i18next"
import { RedirectOnce } from "@/components/redirect"
import { NornMark } from "@/components/shell/norn-mark"
import { ActionBar } from "@/components/ui/action-bar"
import { Button } from "@/components/ui/button"
import { controlClass } from "@/components/ui/control"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PageHeader } from "@/components/ui/page-header"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import { normalizeBaseUrl } from "@/lib/connection"
import { type Notice, useNotice, useT } from "@/lib/i18n"
import { type Auth, safeReturnTo, useSession } from "@/lib/session"
import { cn } from "@/lib/utils"

export function EntryFrame({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: ReactNode
}) {
  const t = useT()
  return (
    <div className="min-h-full bg-background px-6 py-12 md:px-10">
      <Screen width="form">
        <div className="flex items-center gap-2">
          <NornMark className="size-6" />
          <span className="text-sm font-semibold tracking-tight">{t("app.name")}</span>
        </div>
        <PageHeader title={title} description={description} />
        <Section>{children}</Section>
      </Screen>
    </div>
  )
}

/** Whether two cell URLs share an origin; an unparseable URL never does. */
export function sameOrigin(a: string, b: string): boolean {
  try {
    return new URL(a.trim()).origin === new URL(b.trim()).origin
  } catch {
    return false
  }
}

export function ConnectPage() {
  const { auth } = useSession()
  return auth.kind === "oidc" ? <SignInPage auth={auth} /> : <DevConnectPage />
}

/** A configured deployment: sign in with the organization's IdP (authorization code + PKCE). */
function SignInPage({ auth }: { auth: Extract<Auth, { kind: "oidc" }> }) {
  const t = useT()
  const notice = useNotice()
  const search = useSearch({ strict: false }) as { returnTo?: string }
  const returnTo = safeReturnTo(search.returnTo)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<Notice | null>(null)
  if (auth.status === "active") return <RedirectOnce to={returnTo} />
  return (
    <EntryFrame title={t("connect.signInTitle")} description={t("connect.signInDescription")}>
      {auth.status === "expired" ? (
        <p
          role="status"
          className="mb-4 flex items-start gap-2 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning"
        >
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden /> {t("connect.sessionExpired")}
        </p>
      ) : null}
      <Button
        variant="primary"
        className="w-full"
        disabled={pending || auth.status === "loading"}
        onClick={() => {
          setPending(true)
          setError(null)
          auth.signIn(returnTo).catch((err: unknown) => {
            setPending(false)
            setError({ text: err instanceof Error ? err.message : String(err) })
          })
        }}
      >
        {pending || auth.status === "loading" ? <Loader2 className="animate-spin" /> : <LogIn className="rtl-mirror" />}{" "}
        {t("connect.signInButton")}
      </Button>
      {error ? (
        <p role="alert" className="mt-3 text-xs text-destructive">
          {t("connect.signInFailed", { error: notice(error) })}
        </p>
      ) : null}
    </EntryFrame>
  )
}

/** The IdP sends the browser back here with an authorization code; it is redeemed once, then the app opens. */
export function AuthCallbackPage() {
  const t = useT()
  const notice = useNotice()
  const { auth } = useSession()
  const navigate = useNavigate()
  const [error, setError] = useState<Notice | null>(null)
  useEffect(() => {
    if (auth.kind !== "oidc") return
    auth.completeSignIn().then(
      (returnTo) => void navigate({ to: returnTo, replace: true }),
      (err: unknown) => setError({ text: err instanceof Error ? err.message : String(err) }),
    )
    // Runs once per callback URL; completeSignIn itself refuses to redeem a code twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (auth.kind !== "oidc") return <RedirectOnce to="/connect" />
  return (
    <EntryFrame
      title={error ? t("connect.callbackFailedTitle") : t("connect.callbackTitle")}
      description={error ? t("connect.callbackFailedDescription") : t("connect.callbackDescription")}
    >
      {error ? (
        <>
          <p role="alert" className="mb-4 text-xs text-destructive">
            {notice(error)}
          </p>
          <Button variant="primary" onClick={() => void auth.signIn("/")}>
            <LogIn className="rtl-mirror" /> {t("connect.tryAgain")}
          </Button>
        </>
      ) : (
        <Loader2 className="size-5 animate-spin text-muted-foreground" aria-label={t("connect.signingIn")} />
      )}
    </EntryFrame>
  )
}

/** Development mode (no shell configuration): connect with a cell URL and a bearer token. */
function DevConnectPage() {
  const t = useT()
  const notice = useNotice()
  const id = useId()
  const navigate = useNavigate()
  const { connection, connect } = useSession()
  const [baseUrl, setBaseUrl] = useState(connection?.baseUrl ?? "")
  const [token, setToken] = useState(connection?.token ?? "")
  const [actor, setActor] = useState(connection?.actor ?? "")
  const [advanced, setAdvanced] = useState(Boolean(connection?.actor))
  const [error, setError] = useState<Notice | null>(null)
  // A credential belongs to one cell: pointing the form at another origin drops the saved token and actor.
  const [carried, setCarried] = useState(Boolean(connection?.token))

  return (
    <EntryFrame title={t("connect.title")} description={t("connect.description")}>
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          try {
            const next = normalizeBaseUrl(baseUrl)
            // Defence in depth: never submit the previous cell's token to a different origin.
            const keepsOldToken =
              connection !== null &&
              token.trim() === (connection.token ?? "") &&
              token.trim() !== "" &&
              !sameOrigin(next, connection.baseUrl)
            if (keepsOldToken) {
              setToken("")
              setCarried(false)
              setError({ key: "connect.otherCellToken" })
              return
            }
            connect({ baseUrl: next, token: token.trim() || null, actor: advanced ? actor.trim() || null : null })
            void navigate({ to: "/" })
          } catch (err) {
            setError(
              err instanceof TypeError
                ? { key: "connect.invalidUrl" }
                : { text: err instanceof Error ? err.message : String(err) },
            )
          }
        }}
      >
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-url`}>{t("connect.cellUrl")}</Label>
          <Input
            id={`${id}-url`}
            value={baseUrl}
            onChange={(e) => {
              setBaseUrl(e.target.value)
              setError(null)
              if (connection && carried && !sameOrigin(e.target.value, connection.baseUrl)) {
                setToken("")
                setActor("")
                setCarried(false)
              }
            }}
            placeholder="https://cell.example.com"
            inputMode="url"
            autoComplete="url"
            spellCheck={false}
            required
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-url-error` : undefined}
            className={cn(controlClass, "font-mono text-xs")}
          />
          {error ? (
            <p id={`${id}-url-error`} className="text-xs text-destructive">
              {notice(error)}
            </p>
          ) : null}
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-token`}>{t("connect.token")}</Label>
          <Input
            id={`${id}-token`}
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={t("connect.tokenPlaceholder")}
            autoComplete="off"
            spellCheck={false}
            className={cn(controlClass, "font-mono text-xs")}
            aria-describedby={`${id}-token-help`}
          />
          <p id={`${id}-token-help`} className="text-xs text-subtle-foreground">
            {t(connection && !carried && connection.token ? "connect.tokenChangedHelp" : "connect.tokenHelp")}
          </p>
        </div>
        <div>
          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            aria-expanded={advanced}
            className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronRight
              className={`rtl-mirror size-3.5 transition-transform duration-150 ${advanced ? "rotate-90" : ""}`}
            />
            {t("connect.devActor")}
          </button>
          {advanced ? (
            <div className="mt-2 grid gap-1.5">
              <Label htmlFor={`${id}-actor`} className="sr-only">
                {t("connect.devActor")}
              </Label>
              <Input
                id={`${id}-actor`}
                value={actor}
                onChange={(e) => setActor(e.target.value)}
                placeholder="deployer"
                autoComplete="off"
                spellCheck={false}
                className={cn(controlClass, "font-mono text-xs")}
                aria-describedby={`${id}-actor-help`}
              />
              <p id={`${id}-actor-help`} className="text-xs text-subtle-foreground">
                <Trans
                  shouldUnescape
                  tOptions={{ interpolation: { escapeValue: true } }}
                  t={t}
                  i18nKey="connect.devActorFormattedHelp"
                  values={{ header: "x-norn-actor" }}
                  components={{ header: <code className="font-mono" /> }}
                />
              </p>
            </div>
          ) : null}
        </div>
        <ActionBar className="max-md:bottom-0">
          <Button type="submit" variant="primary">
            {t("connect.submit")} <ArrowRight className="rtl-mirror" />
          </Button>
        </ActionBar>
      </form>
    </EntryFrame>
  )
}
